import { useLocale } from '@mirai/locale';
import { Text, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { L10N } from './GooglePay.l10n';
import * as style from './GooglePay.module.css';
import { getPaymentData } from './helpers';
import { loadScript } from '../../../../../../../helpers/loadScript';

let paymentsClient;

export const GooglePay = ({ amount, googlePayId, response, onClose = () => {} }) => {
  const { translate } = useLocale();

  const el = useRef();

  const [ready, setReady] = useState();
  const [visible, setVisible] = useState();

  useEffect(() => {
    let callback;

    (async () => {
      callback = await loadScript({
        domain: 'payments',
        id: 'googlePayScript',
        src: process.env.METHOD_GOOGLE_PAY_SCRIPT,
        onLoad: handleLoad,
      });
    })();

    return () => {
      callback && callback();
    };
  }, []);

  useEffect(() => {
    if (!response || !ready) return;

    // eslint-disable-next-line no-undef
    paymentsClient = new google.payments.api.PaymentsClient({
      environment: process.env.NODE_ENV === 'production' ? 'PRODUCTION' : 'TEST',
    });

    paymentsClient
      .isReadyToPay({ ...baseRequest, allowedPaymentMethods, merchantInfo })
      .then(({ result } = {}) => setVisible(!!result))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error checking if Google Pay is ready to pay:', error);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, response]);

  useEffect(() => {
    if (!visible) return;

    const button = paymentsClient.createButton({ buttonSizeMode: 'fill', onClick: handleClick });
    el.current.appendChild(button);

    return () => {
      if (button.parentNode) button.parentNode.removeChild(button);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleClick = () => {
    paymentsClient
      .loadPaymentData({ ...baseRequest, allowedPaymentMethods, merchantInfo, transactionInfo })
      .then(({ paymentMethodData: { tokenizationData: { token } = {} } = {} }) => {
        onClose();

        const url = new URL(configuration.RETURN_URL);
        url.searchParams.append('paymentMethod', 'GOOGLE_PAY');
        url.searchParams.append('token', token);
        window.location.href = url.toString();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error loading payment data:', error);
      });
  };

  const handleLoad = () => setReady(true);

  const { configuration } = response || {};
  const { allowedPaymentMethods, baseRequest, merchantInfo, transactionInfo } = getPaymentData({
    amount,
    configuration,
    googlePayId,
  });

  return visible ? (
    <View ref={el} className={style.container}>
      <View className={style.divider}>
        <Text action>{translate(L10N.LABEL_PAY_WITH_CARD)}</Text>
      </View>
    </View>
  ) : null;
};

GooglePay.displayName = 'Mirai:Payments:ButtonPayment:FormAddon:GooglePay';

GooglePay.propTypes = {
  amount: PropTypes.number.isRequired,
  googlePayId: PropTypes.string.isRequired,
  response: PropTypes.shape({
    configuration: PropTypes.shape({
      MERCHANT_ID: PropTypes.string.isRequired,
      CURRENCY: PropTypes.string.isRequired,
      HPP_CUSTOMER_COUNTRY: PropTypes.string.isRequired,
      RETURN_URL: PropTypes.string.isRequired,
    }).isRequired,
  }),
  onClose: PropTypes.func,
};
